import {
  invalidOrExpiredClerkTokenErrorCode,
  invalidOrExpiredClerkTokenErrorMessage,
} from '@shared';

type GetTokenOptions = { skipCache?: boolean };
type GetTokenFn = (options?: GetTokenOptions) => Promise<string | null>;

export const clerkTokenUnavailableErrorCode = 'CLERK_TOKEN_UNAVAILABLE';

export class ClerkTokenUnavailableError extends Error {
  public readonly code = clerkTokenUnavailableErrorCode;

  constructor() {
    super('An error occurred interacting with the API.');
    this.name = 'ClerkTokenUnavailableError';
  }
}

let _getClerkToken: GetTokenFn | null = null;
let providerGeneration = 0;
let forcedRefreshPromise: Promise<string | null> | null = null;

export function registerClerkTokenProvider(getToken: GetTokenFn) {
  _getClerkToken = getToken;
  providerGeneration += 1;
  forcedRefreshPromise = null;
}

export function clearClerkTokenProvider() {
  _getClerkToken = null;
  providerGeneration += 1;
  forcedRefreshPromise = null;
}

export function hasClerkTokenProvider(): boolean {
  return _getClerkToken !== null;
}

async function getTokenSafely(
  getToken: GetTokenFn,
  options?: GetTokenOptions,
): Promise<string | null> {
  try {
    return await getToken(options);
  } catch {
    throw new ClerkTokenUnavailableError();
  }
}

export async function getClerkTokenForApi(options?: GetTokenOptions): Promise<string | null> {
  if (!_getClerkToken) return null;
  return getTokenSafely(_getClerkToken, options);
}

export function isInvalidOrExpiredClerkTokenResponseData(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;

  const { code, errorMessage } = data as { code?: unknown; errorMessage?: unknown };
  return (
    code === invalidOrExpiredClerkTokenErrorCode ||
    (code === undefined && errorMessage === invalidOrExpiredClerkTokenErrorMessage)
  );
}

/**
 * Bypasses Clerk's clock-based token cache after the API has rejected a token.
 * Concurrent 401 responses share one refresh so a failed request burst does not
 * amplify into a burst of Clerk token-minting requests.
 */
export async function forceRefreshClerkTokenForApi(): Promise<string | null> {
  const getToken = _getClerkToken;
  if (!getToken) return null;

  if (!forcedRefreshPromise) {
    const refreshGeneration = providerGeneration;
    const refreshPromise = getTokenSafely(getToken, { skipCache: true })
      .then(token => {
        if (providerGeneration !== refreshGeneration || _getClerkToken !== getToken) {
          return null;
        }
        return token;
      })
      .finally(() => {
        if (forcedRefreshPromise === refreshPromise) {
          forcedRefreshPromise = null;
        }
      });
    forcedRefreshPromise = refreshPromise;
  }

  return forcedRefreshPromise;
}
